// src/pages/Swap/hooks/useExchangeRate.js - Fixed direct contract calls for aggregator
/* global BigInt */
import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { ethers, formatUnits, parseUnits } from 'ethers';
import { 
  getBitDogSwapContract, 
  isNativeToken,
  getActualTokenDecimals 
} from '../utils/SwapUtils';
import { isUsingSushiSwap, fetchSushiSwapRoute } from '../../../services/sushiswapApi';
import { SettingsContext } from '../../../contexts/SettingsContext';
import { ETH_ADDRESS } from '../utils/SwapConstants';
import { useTranslation } from 'react-i18next';



/**
 * Hook for fetching and calculating exchange rates with improved error handling
 * @param {object} provider - Ethers provider
 * @param {object} currentChain - Current blockchain network
 * @param {object} inputToken - Input token data
 * @param {object} outputToken - Output token data
 * @param {string} amountIn - Input amount
 * @param {string} userAddress - User's wallet address
 * @param {boolean} isChangingChain - Flag indicating if network is changing
 * @param {function} t - Translation function (optional)
 * @returns {object} Exchange rate and related data
 */
export const useExchangeRate = (
  provider, 
  currentChain, 
  inputToken,
  outputToken,
  amountIn, // We need to react to changes in this parameter
  userAddress,
  isChangingChain = false, // Add this with a default value
) => {
  const { t } = useTranslation();
  // Get slippage from settings context
  const { slippage } = useContext(SettingsContext);
  // Extract aggregator address from chain config
  const aggregatorAddress = currentChain?.aggregatorAddress;
  
  const [exchangeRate, setExchangeRate] = useState(null);
  const [priceImpact, setPriceImpact] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [swapError, setSwapError] = useState(null);
  const [bestRouter, setBestRouter] = useState(null);
  const [bestDestRouter, setBestDestRouter] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [routeSummary, setRouteSummary] = useState(null); // For API calls
  const fetchTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchedAmountRef = useRef(null); // Track last fetched amount for debugging
  const userInputActiveRef = useRef(false); // Track if user is actively typing
  const lastInputTimeRef = useRef(0); // Track when the user last typed

  /**
   * Safely parse and validate amount
   * @param {string} amount - Amount to validate
   * @returns {boolean} - True if amount is valid
   */
  const isValidAmount = (amount) => {
    if (!amount) return false;
    const num = parseFloat(amount);
    return !isNaN(num) && num > 0;
  };

  /**
   * Calculate price impact by comparing a small quote with the actual quote
   */
  const calculatePriceImpact = async (
    aggregator, 
    inputAddress, 
    outputAddress, 
    inputDecimals, 
    outputDecimals, 
    amountToUse
  ) => {
    try {
      // Skip calculation for very small amounts
      if (!amountToUse || parseFloat(amountToUse) <= 0) {
        return 0;
      }

      // Ensure decimals are numbers, not strings
      const inputDecimalsNum = typeof inputDecimals === 'string' 
        ? parseInt(inputDecimals, 10) 
        : inputDecimals;
      
      const outputDecimalsNum = typeof outputDecimals === 'string' 
        ? parseInt(outputDecimals, 10) 
        : outputDecimals;

      // 1. Get a quote for a very small amount (to determine the "perfect" market price)
      const smallAmount = parseUnits("0.01", inputDecimalsNum);
      
      // 2. Get a quote for the actual amount the user wants to trade
      const actualAmount = parseUnits(amountToUse.toString(), inputDecimalsNum);
      
      // Skip if the actual amount is too small
      if (actualAmount <= smallAmount) {
        return 0;
      }

      let smallQuote, actualQuote;
      
      // Get quotes based on token types
      if (isNativeToken(inputAddress) && !isNativeToken(outputAddress)) {
        const smallResult = await aggregator.getEthToTokenQuote(outputAddress, smallAmount);
        smallQuote = smallResult[1];
        
        const actualResult = await aggregator.getEthToTokenQuote(outputAddress, actualAmount);
        actualQuote = actualResult[1];
      } 
      else if (!isNativeToken(inputAddress) && isNativeToken(outputAddress)) {
        const smallResult = await aggregator.getTokenToEthQuote(inputAddress, smallAmount);
        smallQuote = smallResult[1];
        
        const actualResult = await aggregator.getTokenToEthQuote(inputAddress, actualAmount);
        actualQuote = actualResult[1];
      } 
      else {
        const smallResult = await aggregator.getTokenToTokenQuote(inputAddress, outputAddress, smallAmount);
        smallQuote = smallResult[2];
        
        const actualResult = await aggregator.getTokenToTokenQuote(inputAddress, outputAddress, actualAmount);
        actualQuote = actualResult[2];
      }
      
      // Convert results to normalized values - handling BigInt properly
      const smallQuoteFormatted = formatUnits(smallQuote, outputDecimalsNum);
      const actualQuoteFormatted = formatUnits(actualQuote, outputDecimalsNum);
      
      // Convert amount values to a consistent base
      const smallAmountFormatted = formatUnits(smallAmount, inputDecimalsNum);
      const actualAmountFormatted = formatUnits(actualAmount, inputDecimalsNum);
      
      // Calculate the expected rate from the small trade (minimal price impact)
      const baseExchangeRate = parseFloat(smallQuoteFormatted) / parseFloat(smallAmountFormatted);
      
      // Calculate the expected output for the full amount based on the base exchange rate 
      const expectedOutput = parseFloat(actualAmountFormatted) * baseExchangeRate;
      
      // The actual output we would get with price impact
      const actualOutput = parseFloat(actualQuoteFormatted);
      
      // Calculate price impact: how much less we're getting compared to the expected amount
      const impact = ((expectedOutput - actualOutput) / expectedOutput) * 100;
      
      // Ensure we return a non-negative value and limit to two decimal places
      return Math.max(0, parseFloat(impact.toFixed(2)));
    } catch (error) {
      console.error("Error calculating price impact:", error);
      // Return 0.5% as a fallback
      return 0.5;
    }
  };


/**
 * Fetch exchange rate using SushiSwap API
 * @param {boolean} force - Force refresh
 */
const fetchRateFromSushiSwap = async (force = false) => {
  if (!inputToken?.symbol || !outputToken?.symbol || !currentChain) {
    setExchangeRate(null);
    setRouteSummary(null);
    return;
  }
  
  // Skip if trying to swap the same token
  if (inputToken.address?.toLowerCase() === outputToken.address?.toLowerCase()) {
    setExchangeRate(1);
    setPriceImpact(0);
    return;
  }
  
  // For quotes (not actual swaps) we can use the quote endpoint
  // which doesn't check allowance or balance
  setIsLoading(true);
  setSwapError(null);
  
  try {
    // Check if we have valid tokens and amount
    if (!inputToken.address) throw new Error(t('swap.errors.invalidToken'));
    if (!outputToken.address) throw new Error(t('swap.errors.invalidToken'));
    
    // Use amountIn if available, otherwise use 1 token for rate calculation
    // When calculating rate with a fixed amount like 1, we don't need to worry about balance
    const amountToUse = isValidAmount(amountIn) ? amountIn : '1';
    
    // Store the amount being fetched for debugging
    lastFetchedAmountRef.current = amountToUse;
    
    // For rate calculation with actual amounts, handle slippage and gas consideration
    let effectiveAmount = amountToUse;
    let isCheckingRate = !isValidAmount(amountIn);
    
    // If checking actual swap (not just rate), reduce native token amount slightly to account for gas
    if (!isCheckingRate && isNativeToken(inputToken.address)) {
      const amountNum = parseFloat(amountToUse);
      if (amountNum > 0.01) {
        // Reduce by 5% to ensure gas coverage
        effectiveAmount = (amountNum * 0.95).toString();
        console.log(`Using reduced amount for native token quote: ${amountToUse} -> ${effectiveAmount}`);
      }
    }
    
    console.log("Fetching exchange quote for:", {
      inputToken: inputToken.symbol,
      inputAddress: inputToken.address,
      outputToken: outputToken.symbol,
      outputAddress: outputToken.address,
      amountToUse: effectiveAmount,
      isRateCheck: isCheckingRate
    });
    
    // Import the quote function to avoid circular dependencies
    const { fetchSushiSwapQuote } = await import('../../../services/sushiswapApi');
    
    // Call SushiSwap Quote API - This doesn't check allowance or balance
    const quoteResponse = await fetchSushiSwapQuote(
      currentChain,
      inputToken, 
      outputToken, 
      effectiveAmount,
      {
        clientId: 'bitdog-wallet',
        slippage: isCheckingRate ? '0.5' : slippage
      }
    );
    
    // Create a mock routeSummary since the quote endpoint doesn't provide tx data
    // This is just for rate calculations, not actual transaction execution
    const mockRouteSummary = {
      tokenIn: inputToken.address,
      tokenOut: outputToken.address,
      amountIn: quoteResponse.amountIn,
      amountOut: quoteResponse.assumedAmountOut,
      _tx: null // No TX data from quote endpoint
    };
    
    setRouteSummary(mockRouteSummary);
    
    // Get decimals for output token - ensure it's a number
    const outputDecimals = typeof outputToken.decimals === 'string' 
      ? parseInt(outputToken.decimals, 10) 
      : (outputToken.decimals || 18);
    
    // Calculate exchange rate using the assumedAmountOut
    const outputAmount = quoteResponse.assumedAmountOut;
    const outputAmountFormatted = formatUnits(outputAmount, outputDecimals);
    
    // If we used 1 token for quote, this is the exchange rate
    if (!isValidAmount(amountIn)) {
      setExchangeRate(parseFloat(outputAmountFormatted));
    }
    // Otherwise calculate based on actual input amount
    else {
      setExchangeRate(parseFloat(outputAmountFormatted) / parseFloat(amountIn));
    }
    
    // Extract price impact from response
    const priceImpactValue = quoteResponse.priceImpact !== undefined 
      ? Math.abs(parseFloat(quoteResponse.priceImpact) * 100) 
      : 0.5;
    
    setPriceImpact(priceImpactValue);
    
  } catch (err) {
    console.error("Error fetching exchange rate:", err);
    setExchangeRate(null);
    setRouteSummary(null);
    
    // Improve error message for insufficient balance
    if (err.message && err.message.includes("Insufficient") && err.message.includes("balance")) {
      setSwapError(`${err.message} ${t('swap.errors.trySmaller')}`);
    } else {
      setSwapError(t('swap.errors.rateError', { message: err.message }));
    }
    
    setBestRouter(null);
    setBestDestRouter(null);
  } finally {
    setIsLoading(false);
  }
};

  /**
 * Fetch exchange rate using direct contract calls
 * @param {boolean} force - Force refresh
 */
const fetchRateFromContract = async (force = true) => {
  // Prevent recursive and parallel fetches
  if (isFetchingRef.current && !force) {
    console.log("Already fetching rate, aborting");
    return;
  }
  
  // Only proceed if we have both tokens
  if (!inputToken?.symbol || !outputToken?.symbol || !provider || !aggregatorAddress) {
    setExchangeRate(null);
    setBestRouter(null);
    setBestDestRouter(null);
    return;
  }
  
  // Skip if trying to swap the same token
  if (inputToken.address?.toLowerCase() === outputToken.address?.toLowerCase()) {
    setExchangeRate(1);
    setPriceImpact(0);
    return;
  }
  
  setIsLoading(true);
  setSwapError(null);
  
  try {
    // CRITICAL FIX: More robust contract instantiation and error handling
    const aggregator = getBitDogSwapContract(provider, aggregatorAddress, null);
    if (!aggregator) {
      throw new Error(t('swap.errors.aggregatorContractFailed'));
    }
    
    // Log contract info for debugging
    console.log("Using aggregator contract at:", aggregatorAddress);
    
    try {
      // Test if contract is responsive with a simple call
      await aggregator.getAddress();
      console.log("Aggregator contract is responsive");
    } catch (contractError) {
      console.error("Failed to connect to aggregator contract:", contractError);
      throw new Error(t('swap.errors.exchangeServiceFailed'));
    }
    
    // Get decimals for both tokens
    let inputDecimals = await getActualTokenDecimals(provider, inputToken);
    let outputDecimals = await getActualTokenDecimals(provider, outputToken);
    
    // Ensure decimals are numbers, not strings
    inputDecimals = typeof inputDecimals === 'string' 
      ? parseInt(inputDecimals, 10) 
      : inputDecimals;
      
    outputDecimals = typeof outputDecimals === 'string' 
      ? parseInt(outputDecimals, 10) 
      : outputDecimals;
      
    // CRITICAL FIX: Ensure decimals don't exceed maximum allowed (18 for ETH)
    if (isNativeToken(inputToken.address) && inputDecimals > 18) {
      console.warn(`Limiting input decimals from ${inputDecimals} to 18 for ETH`);
      inputDecimals = 18;
    }
    
    if (isNativeToken(outputToken.address) && outputDecimals > 18) {
      console.warn(`Limiting output decimals from ${outputDecimals} to 18 for ETH`);
      outputDecimals = 18;
    }
      
    console.log("Token decimals:", {
      inputToken: inputToken.symbol,
      inputDecimals: inputDecimals,
      outputToken: outputToken.symbol,
      outputDecimals: outputDecimals
    });
    
    // Use addresses directly - the special ETH_ADDRESS for native tokens
    const inputAddress = inputToken.address;
    const outputAddress = outputToken.address;
    
    // Use amountIn if available, otherwise use 1 token for rate calculation
    const amountToUse = isValidAmount(amountIn) ? amountIn : '1';
    
    // Store the amount being fetched for debugging
    lastFetchedAmountRef.current = amountToUse;
    
    // Ensure amount is a string before parsing
    let amountInWei;
    try {
      amountInWei = parseUnits(amountToUse.toString(), inputDecimals);
    } catch (error) {
      // Handle underflow errors for very small amounts
      if (error.code === 'NUMERIC_FAULT' && error.fault === 'underflow') {
        console.warn(`Value too small for token decimals: ${amountToUse}, using minimum value`);
        amountInWei = BigInt(1);
      } else {
        throw error;
      }
    }
          
    console.log("Fetching quote for:", {
      inputToken: inputToken.symbol,
      outputToken: outputToken.symbol,
      inputAddress,
      outputAddress,
      amountInWei: amountInWei.toString(),
      inputDecimals,
      outputDecimals
    });
    
    // Get quote based on token types
    let estimatedAmount, router, destRouter;
    
    try {
      // ETH to Token
      if (isNativeToken(inputAddress) && !isNativeToken(outputAddress)) {
        console.log("Getting ETH to Token quote");
        const result = await aggregator.getEthToTokenQuote(outputAddress, amountInWei);
        router = result[0];
        estimatedAmount = result[1];
        setBestRouter(router);
        setBestDestRouter(null);
      }
      // Token to ETH
      else if (!isNativeToken(inputAddress) && isNativeToken(outputAddress)) {
        console.log("Getting Token to ETH quote");
        const result = await aggregator.getTokenToEthQuote(inputAddress, amountInWei);
        router = result[0];
        estimatedAmount = result[1];
        setBestRouter(router);
        setBestDestRouter(null);
      }
      // Token to Token
      else {
        console.log("Getting Token to Token quote");
        const result = await aggregator.getTokenToTokenQuote(
          inputAddress,
          outputAddress,
          amountInWei
        );
        router = result[0];
        destRouter = result[1];
        estimatedAmount = result[2];
        setBestRouter(router);
        setBestDestRouter(destRouter);
      }
    } catch (quoteError) {
      console.error("Error getting quote:", quoteError);
      
      // Extract meaningful error message
      let errorMessage = quoteError.message || "Unknown error";
      
      // Check for common contract error patterns
      if (errorMessage.includes("revert")) {
        if (errorMessage.includes("insufficient liquidity")) {
          throw new Error(t('swap.errors.insufficientLiquidity'));
        } else if (errorMessage.includes("invalid token")) {
          throw new Error(t('swap.errors.invalidToken'));
        } else if (errorMessage.includes("zero address")) {
          throw new Error(t('swap.errors.invalidRouter'));
        } else {
          throw new Error(t('swap.errors.rateError', { 
            message: errorMessage.split("reason=")[1]?.split('"')[1] || "Transaction would fail"
          }));
        }
      } else if (errorMessage.includes("header not found") || errorMessage.includes("network error")) {
        throw new Error(t('swap.errors.networkConnection'));
      } else {
        throw new Error(t('swap.errors.quoteError', { message: errorMessage }));
      }
    }
    
    if (!router || router === ethers.ZeroAddress) {
      throw new Error(t('swap.errors.noLiquidity'));
    }
    
    // Calculate exchange rate with added error handling
    let outputAmount;
    try {
      outputAmount = formatUnits(estimatedAmount, outputDecimals);
    } catch (error) {
      // Handle any formatting errors
      if (error.code === 'NUMERIC_FAULT') {
        console.warn(`Formatting error for estimated amount: ${error.message}, using fallback calculation`);
        // Fallback calculation - convert to number and divide manually
        const rawEstimate = Number(estimatedAmount.toString());
        const divisor = Math.pow(10, outputDecimals);
        outputAmount = (rawEstimate / divisor).toString();
      } else {
        throw error;
      }
    }
    
    // If we used 1 token for quote, this is the exchange rate
    if (!isValidAmount(amountIn)) {
      setExchangeRate(parseFloat(outputAmount));
    }
    // Otherwise calculate based on actual input amount
    else {
      setExchangeRate(parseFloat(outputAmount) / parseFloat(amountIn));
    }
    
    // Calculate price impact based on actual trade size
    const impact = await calculatePriceImpact(
      aggregator,
      inputAddress,
      outputAddress,
      inputDecimals,
      outputDecimals,
      amountToUse
    );
    
    console.log("Calculated price impact:", impact, "%");
    setPriceImpact(impact);
    
  } catch (err) {
    console.error("Error fetching exchange rate:", err);
    setExchangeRate(null);
    
    // Provide more helpful error messages
    if (err.message.includes("insufficient liquidity")) {
      setSwapError(t('swap.errors.insufficientLiquidity'));
    } else if (err.message.includes("invalid token")) {
      setSwapError(t('swap.errors.invalidTokenForNetwork'));
    } else if (err.message.includes("Could not initialize")) {
      setSwapError(t('swap.errors.exchangeServiceFailed'));
    } else if (err.message.includes("network connection")) {
      setSwapError(t('swap.errors.networkConnection'));
    } else {
      setSwapError(t('swap.errors.rateError', { message: err.message }));
    }
    
    setBestRouter(null);
    setBestDestRouter(null);
  } finally {
    setIsLoading(false);
  }
};

  /**
 * Fetch exchange rate specifically for wrapped native → ERC20 swaps
 * This works around a contract issue by simulating the swap as unwrap + swap
 */
const fetchRateForWrappedNative = async (force = false) => {
  // Only proceed if we have the required tokens and data
  if (!inputToken?.symbol || !outputToken?.symbol || !provider || !currentChain) {
    return;
  }
  
  const wrappedNativeAddress = currentChain.wrappedNativeToken?.toLowerCase();
  if (!wrappedNativeAddress) return;
  
  // Check if this is indeed a wrapped native → ERC20 swap
  const inputAddress = inputToken.address?.toLowerCase();
  const outputAddress = outputToken.address?.toLowerCase();
  
  // Only handle the specific case: wrapped native → ERC20 (not native, not wrapped native)
  if (
    inputAddress !== wrappedNativeAddress || 
    isNativeToken(outputAddress) || 
    outputAddress === wrappedNativeAddress
  ) {
    return;
  }
  
  console.log("Using special handling for wrapped native → ERC20 swap");
  setIsLoading(true);
  setSwapError(null);
  
  try {
    // Step 1: Simulate as if we're using native token instead of wrapped
    const nativeToken = {
      ...inputToken,
      address: ETH_ADDRESS, // Use the native token address
      symbol: currentChain.nativeToken.symbol
    };
    
    console.log("Simulating native token swap instead of wrapped");
    
    // Get decimals 
    const inputDecimals = await getActualTokenDecimals(provider, inputToken);
    const outputDecimals = await getActualTokenDecimals(provider, outputToken);
    
    // Parse amount for quote
    const amountToUse = isValidAmount(amountIn) ? amountIn : '1';
    const amountInWei = parseUnits(amountToUse.toString(), inputDecimals);
    
    // Get the aggregator contract
    const aggregator = getBitDogSwapContract(provider, aggregatorAddress, null);
    if (!aggregator) {
      throw new Error(t('swap.errors.aggregatorContractFailed'));
    }
    
    // Get ETH to Token quote (as if we first unwrapped the token)
    console.log("Getting ETH to Token quote as workaround");
    const result = await aggregator.getEthToTokenQuote(outputAddress, amountInWei);
    const router = result[0];
    const estimatedAmount = result[1];
    
    if (!router || router === ethers.ZeroAddress) {
      throw new Error(t('swap.errors.noLiquidity'));
    }
    
    // Set the router info (for swap execution later)
    setBestRouter(router);
    setBestDestRouter(null);
    
    // Calculate exchange rate
    const outputAmount = formatUnits(estimatedAmount, outputDecimals);
    
    // Update the exchange rate based on the quote
    if (!isValidAmount(amountIn)) {
      setExchangeRate(parseFloat(outputAmount));
    } else {
      setExchangeRate(parseFloat(outputAmount) / parseFloat(amountIn));
    }
    
    // Set a default price impact or calculate it if possible
    // For simplicity, we set a standard 0.5% price impact which is typical
    setPriceImpact(0.5);
    
    console.log("Workaround successful - found route for wrapped native swap");
  } catch (err) {
    console.error("Error in wrapped native workaround:", err);
    setExchangeRate(null);
    setSwapError(t('swap.errors.rateError', { message: err.message }));
    setBestRouter(null);
    setBestDestRouter(null);
  } finally {
    setIsLoading(false);
  }
};

// Function to fetch exchange rate with improved debouncing
const fetchExchangeRate = useCallback(async (force = true) => {
  // CRITICAL NEW CHECK: Skip completely for wrap/unwrap tokens
  // Check if this is a wrap/unwrap operation (native ⟷ wrapped native)
  if (currentChain?.wrappedNativeToken && inputToken?.address && outputToken?.address) {
    const wrappedNativeAddress = currentChain.wrappedNativeToken.toLowerCase();
    const inputAddress = inputToken.address.toLowerCase();
    const outputAddress = outputToken.address.toLowerCase();
    
    // Case 1: Native to Wrapped (WRAP)
    if (isNativeToken(inputAddress) && outputAddress === wrappedNativeAddress) {
      console.log("Skipping quote fetching - this is a WRAP operation");
      setExchangeRate(1);
      setPriceImpact(0);
      setIsLoading(false);
      setSwapError(null);
      return;
    }
    
    // Case 2: Wrapped to Native (UNWRAP)
    if (inputAddress === wrappedNativeAddress && isNativeToken(outputAddress)) {
      console.log("Skipping quote fetching - this is an UNWRAP operation");
      setExchangeRate(1);
      setPriceImpact(0);
      setIsLoading(false);
      setSwapError(null);
      return;
    }
  }
  
  // Prevent recursive and parallel fetches
  if (isFetchingRef.current && !force) {
    console.log("Already fetching rate, aborting");
    return;
  }
  
  // Clear any pending fetch
  if (fetchTimeoutRef.current) {
    clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = null;
  }
  
  // Only proceed if we have both tokens
  if (!inputToken?.symbol || !outputToken?.symbol || !provider || !currentChain) {
    setExchangeRate(null);
    setBestRouter(null);
    setBestDestRouter(null);
    setRouteSummary(null);
    return;
  }
  
  // Skip if trying to swap the same token
  if (inputToken.address?.toLowerCase() === outputToken.address?.toLowerCase()) {
    setExchangeRate(1);
    setPriceImpact(0);
    return;
  }
  
  // CRITICAL: For API calls, verify userAddress is available
  if (isUsingSushiSwap(currentChain) && !userAddress) {
    console.error("User address is required for exchange rate");
    setSwapError(t('swap.errors.connectWallet'));
    return;
  }
  
  // Check if this is a wrapped native → ERC20 swap that needs special handling
  const isWrappedNativeToErc20 = currentChain?.wrappedNativeToken && 
    inputToken?.address?.toLowerCase() === currentChain.wrappedNativeToken.toLowerCase() &&
    outputToken?.address && 
    !isNativeToken(outputToken.address) &&
    outputToken.address.toLowerCase() !== currentChain.wrappedNativeToken.toLowerCase();

  if (isWrappedNativeToErc20 && !isUsingSushiSwap(currentChain)) {
    console.log("Detected wrapped native → ERC20 swap, using workaround");
    fetchTimeoutRef.current = setTimeout(() => {
      fetchRateForWrappedNative(force);
    }, 500);
    return; // Exit early to avoid standard fetch
  }
  
  // Debug log to trace amount changes
  console.log(`Fetching exchange rate with amount: ${amountIn}`);
  
  // Set up a debounce timeout - longer for better typing experience
  fetchTimeoutRef.current = setTimeout(async () => {
    // Check if user is actively typing
    const timeSinceLastInput = Date.now() - lastInputTimeRef.current;
    if (userInputActiveRef.current && timeSinceLastInput < 1500 && !force) {
      console.log("User is actively typing, delaying fetch");
      return;
    }
    
    // Set flag to prevent re-entrant fetches
    if (isFetchingRef.current && !force) {
      return;
    }
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setSwapError(null);
    
    try {
      // Determine which method to use based on chain config
      if (isUsingSushiSwap(currentChain)) {
        await fetchRateFromSushiSwap(force);
      } else {
        await fetchRateFromContract(force);
      }
    } catch (error) {
      console.error("Error in fetchExchangeRate:", error);
      setSwapError(t('swap.errors.rateError', { message: error.message }));
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
      userInputActiveRef.current = false;
    }
  }, 2000); // Longer debounce for better typing experience
  
}, [
  inputToken?.symbol, 
  inputToken?.address,
  outputToken?.symbol, 
  outputToken?.address,
  provider, 
  currentChain,
  currentChain?.wrappedNativeToken, // Add this to dependencies for wrap/unwrap checking
  aggregatorAddress,
  userAddress,
  amountIn, // CRITICAL: Include amountIn in the dependencies!
  slippage,
  t // Include the translation function in dependencies
]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      isFetchingRef.current = false;
    };
  }, []);

  // Add a tracking effect for user input
  useEffect(() => {
    // Update typing status when amountIn changes
    if (amountIn !== undefined) {
      userInputActiveRef.current = true;
      lastInputTimeRef.current = Date.now();
    }
  }, [amountIn]);

  // Modified useEffect that now depends on amountIn but has better debouncing
  useEffect(() => {
    if (inputToken?.symbol && outputToken?.symbol && !isChangingChain) {
      // Skip exchange rate fetching for wrap/unwrap operations
      if (currentChain?.wrappedNativeToken && inputToken?.address && outputToken?.address) {
        const wrappedNativeAddress = currentChain.wrappedNativeToken.toLowerCase();
        const inputAddress = inputToken.address.toLowerCase();
        const outputAddress = outputToken.address.toLowerCase();
        
        // Check if this is a wrap/unwrap operation
        if ((isNativeToken(inputAddress) && outputAddress === wrappedNativeAddress) ||
            (inputAddress === wrappedNativeAddress && isNativeToken(outputAddress))) {
          // For wrap/unwrap, set fixed 1:1 exchange rate and skip API calls
          setExchangeRate(1);
          setPriceImpact(0);
          setIsLoading(false);
          return;
        }
      }
      
      // Check if we need a wallet address (for SushiSwap) and have one
      const isSushiSwapChain = isUsingSushiSwap(currentChain);
      const needsWalletAddress = isSushiSwapChain;
      const hasWalletAddress = !!userAddress;
      
      // Only fetch if we don't need a wallet address or we have one
      if (!needsWalletAddress || hasWalletAddress) {
        console.log(`Triggering fetch with amount: ${amountIn}`);
        const fetchTimeout = setTimeout(() => {
          fetchExchangeRate();
        }, 200);
        
        return () => clearTimeout(fetchTimeout);
      } else if (needsWalletAddress && !hasWalletAddress) {
        // If SushiSwap is needed but we don't have an address, show clear error
        setSwapError(t('swap.errors.connectWallet'));
      }
    }
  }, [
    inputToken?.symbol, 
    inputToken?.address,
    outputToken?.symbol, 
    outputToken?.address,
    fetchExchangeRate, 
    isChangingChain, 
    currentChain, 
    currentChain?.wrappedNativeToken,
    userAddress,
    amountIn, // CRITICAL: Include amountIn here too
    t // Include translation function
  ]);

  // Manual refresh button handler
  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      userInputActiveRef.current = true; // Force fetch even if user was just typing
      await fetchExchangeRate(true); // Force refresh
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    exchangeRate,
    priceImpact,
    isLoading,
    swapError,
    bestRouter,
    bestDestRouter,
    routeSummary,
    isRefreshing,
    fetchExchangeRate,
    handleManualRefresh,
    setSwapError,
    lastFetchedAmount: lastFetchedAmountRef.current,
    setRouteSummary,
    setBestRouter,
    setBestDestRouter,
    setExchangeRate, // Add this to allow direct control
    setPriceImpact    // Add this to allow direct control
  };
};

export default useExchangeRate;